/**
 * Utility helpers for normalising cost-centre ID strings in report API payloads.
 *
 * Cost-centre dropdown labels are stored as "<code>-<description>"
 * (e.g. "5109-Local-Administration---B").  Only the code prefix before the
 * **first** hyphen is meaningful to back-end report APIs.
 *
 * Default separator is `","`.  Use `"~"` only for the
 * Export UBT Details (by-criteria) API which requires tilde-delimited IDs.
 */

/** Multi-select state shape shared by all cost-centre Redux/session fields. */
export interface CostCenterSelection {
  isAllSelected: boolean;
  selectedIds: (string | number)[];
}

/**
 * Extracts the cost-centre code from a raw label string.
 *
 * Splits on the first hyphen and returns the trimmed prefix.
 * When no hyphen is present the original value is returned unchanged.
 *
 * @param raw - Raw cost-centre label, e.g. `"5109-Local-Administration---B"`.
 * @returns The code portion, e.g. `"5109"`.
 *
 * @example
 * extractCostCenterId("5109-Local-Administration---B"); // "5109"
 * extractCostCenterId("6177-CCA-Statewide");            // "6177"
 * extractCostCenterId("CORP");                          // "CORP"  (no hyphen)
 */
export function extractCostCenterId(raw: string): string {
  const idx = raw.indexOf("-");
  return idx > 0 ? raw.substring(0, idx).trim() : raw;
}

/**
 * Formats a collection of raw cost-centre IDs into a delimited string for an
 * API query parameter.
 *
 * Each item is passed through {@link extractCostCenterId} so that full label
 * strings are reduced to their code prefix before joining.
 *
 * @param ids       - Raw cost-centre IDs from the multi-select field.
 * @param separator - Delimiter to use between codes (default `","`).
 * @returns Delimited string, e.g. `"5109,6109,6177"` or `"5109~6109~6177"`.
 *
 * @example
 * formatCostCenterIds(["5109-Local-Admin", "6109-UNALLOC"], ","); // "5109,6109"
 * formatCostCenterIds(["5109-Local-Admin", "6109-UNALLOC"], "~"); // "5109~6109"
 */
export function formatCostCenterIds(
  ids: (string | number)[],
  separator: "," | "~" = ",",
): string {
  return ids.map((id) => extractCostCenterId(String(id))).join(separator);
}

/**
 * Resolves the API cost-centre parameter value from a multi-select state object.
 *
 * - Returns `"All"` when `isAllSelected` is true or the selection is absent/null.
 * - Returns `""` (empty string) when `isAllSelected` is false but no IDs are chosen.
 * - Otherwise returns a delimited string of extracted code prefixes.
 *
 * @param selected  - Multi-select state from Redux store or session storage.
 * @param separator - Delimiter to use between codes (default `","`).
 * @returns Formatted cost-centre string ready for use in API params.
 *
 * @example
 * getCostCenterParam({ isAllSelected: true, selectedIds: [] });
 * // "All"
 *
 * getCostCenterParam({ isAllSelected: false, selectedIds: ["5109-Local-Admin", "6109-UNALLOC"] });
 * // "5109,6109"
 *
 * getCostCenterParam({ isAllSelected: false, selectedIds: ["5109-Local-Admin"] }, "~");
 * // "5109"
 *
 * getCostCenterParam({ isAllSelected: false, selectedIds: [] });
 * // ""
 */
export function getCostCenterParam(
  selected: CostCenterSelection | undefined | null,
  separator: "," | "~" = ",",
): string {
  if (!selected || selected.isAllSelected) return "All";
  if (!selected.selectedIds?.length) return "";
  return formatCostCenterIds(selected.selectedIds, separator);
}
